import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, BarChart3 } from "lucide-react";
import { getPlanVsActualReport } from "@/api/reports";
import { useCostUnit } from "@/hooks/useCostUnit";
import { formatCurrency } from "@/lib/format";
import { formatNumber } from "@/features/reports/components/chartTheme";
import { EmptyState } from "@/components/ui/EmptyState";
import { KpiCard } from "@/components/ui/KpiCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { PlanVsActualBarChart } from "@/features/reports/components/PlanVsActualBarChart";
import { toneClasses } from "@/components/ui/badgeTones";
import type { PlanVsActualReport, ReportParams } from "@/types/reports";

interface Props {
  /** Shared analytics filter params (date range, vehicle, driver). */
  params: ReportParams;
}

const DASH = "—";

/** Null-guards money before formatCurrency (Sprint 107 NaN lesson). */
function money(
  amount: number | null,
  currency: string,
  locale: string,
): string {
  if (amount == null) return DASH;
  return formatCurrency(amount, currency, locale);
}

/**
 * Null-guarded numeric metric (distance/time) for a KPI card. `null` → em-dash;
 * otherwise a locale-formatted number with its unit suffix.
 */
function metric(value: number | null, unit: string, language: string): string {
  if (value == null) return DASH;
  return formatNumber(value, { language, unit });
}

/** Signed money label for profit/loss & trend; null → em-dash. */
function signedMoney(
  amount: number | null,
  currency: string,
  locale: string,
): string {
  if (amount == null) return DASH;
  const formatted = formatCurrency(Math.abs(amount), currency, locale);
  if (amount > 0) return `+${formatted}`;
  if (amount < 0) return `-${formatted}`;
  return formatCurrency(0, currency, locale);
}

/** Maps a signed money figure to KpiCard's semantic value tone. */
function signedTone(
  amount: number | null,
): "negative" | "neutral" | "positive" {
  if (amount == null || amount === 0) return "neutral";
  return amount > 0 ? "positive" : "negative";
}

/**
 * Directional ↑/↓ badge for a signed money figure. Emerald + ArrowUp for
 * positive, rose + ArrowDown for negative; nothing for null/zero (neutral).
 */
function DeltaBadge({ amount }: { amount: number | null }) {
  const { t } = useTranslation();
  if (amount == null || amount === 0) return null;
  const positive = amount > 0;
  const Arrow = positive ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold",
        toneClasses(positive ? "success" : "danger"),
      )}
    >
      <Arrow className="h-3 w-3" aria-hidden="true" strokeWidth={2.5} />
      {positive ? t("analytics.pva.up") : t("analytics.pva.down")}
    </span>
  );
}

/** True when every figure in the report is null → nothing to show. */
function isAllNull(r: PlanVsActualReport): boolean {
  return (
    r.total_planned_cost == null &&
    r.total_actual_cost == null &&
    r.avg_planned_cost_per_order == null &&
    r.avg_actual_cost_per_order == null &&
    r.profit_loss == null &&
    r.trend == null &&
    r.planned_distance_km == null &&
    r.actual_distance_km == null &&
    r.planned_duration_min == null &&
    r.actual_duration_min == null
  );
}

const KPI_COUNT = 6;

/**
 * Planned vs Actual analytics tab (TASK-3829). Renders a six-card KPI strip
 * (plan/actual cost, plan/actual cost-per-order, profit/loss, trend) over two
 * Planned-vs-Actual bar charts (distance in km, time in hours). All money is
 * null-guarded to an em-dash; profit/loss and the trend delta are colored
 * emerald (positive) / rose (negative). Wired to GET
 * /api/v1/reports/plan-vs-actual/ via TanStack Query, respecting the shared
 * date/vehicle/driver filter bar passed down from the Analytics shell.
 */
export function PlanVsActualTab({ params }: Props) {
  const { t, i18n } = useTranslation();
  const currency = useCostUnit();
  const locale = i18n.language;

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "plan-vs-actual", params],
    queryFn: () => getPlanVsActualReport(params),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: KPI_COUNT }).map((_, i) => (
            <KpiCard key={i} label="" value={undefined} />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
            >
              <Skeleton className="mb-2 h-3 w-24" />
              <Skeleton className="h-56 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isAllNull(data)) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <EmptyState
          icon={BarChart3}
          title={t("analytics.pva.noData")}
          subtitle={t("analytics.pva.noDataHint")}
        />
      </div>
    );
  }

  // Minutes → hours for the time chart (null stays null).
  const plannedHours =
    data.planned_duration_min == null ? null : data.planned_duration_min / 60;
  const actualHours =
    data.actual_duration_min == null ? null : data.actual_duration_min / 60;

  // Per-metric presence flags. Distance/time KPI cards are surfaced only when
  // that metric pair actually has data (TASK-3847), so the strip never reads as
  // entirely "no data" while the charts below show real distance/time values —
  // the cards and charts now tell the same story.
  const hasDistance =
    data.planned_distance_km != null || data.actual_distance_km != null;
  const hasDuration = plannedHours != null || actualHours != null;

  return (
    <div className="space-y-4">
      {/* KPI strip — null/loading treatment + semantic Profit/Loss color is
          owned by TASK-3839. Cost cards keep their per-card "—"/no-data
          treatment; distance/time cards (TASK-3847) appear only when that
          metric pair has data so the strip agrees with the charts below. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label={t("analytics.pva.totalPlanCost")}
          hint={t("analytics.pva.totalPlanCostHint")}
          value={money(data.total_planned_cost, currency, locale)}
        />
        <KpiCard
          label={t("analytics.pva.totalActualCost")}
          hint={t("analytics.pva.totalActualCostHint")}
          value={money(data.total_actual_cost, currency, locale)}
        />
        <KpiCard
          label={t("analytics.pva.avgPlanPerOrder")}
          hint={t("analytics.pva.avgPlanPerOrderHint")}
          value={money(data.avg_planned_cost_per_order, currency, locale)}
        />
        <KpiCard
          label={t("analytics.pva.avgActualPerOrder")}
          hint={t("analytics.pva.avgActualPerOrderHint")}
          value={money(data.avg_actual_cost_per_order, currency, locale)}
        />
        <KpiCard
          label={t("analytics.pva.profitLoss")}
          hint={t("analytics.pva.profitLossHint")}
          value={signedMoney(data.profit_loss, currency, locale)}
          valueTone={signedTone(data.profit_loss)}
          badge={<DeltaBadge amount={data.profit_loss} />}
        />
        <KpiCard
          label={t("analytics.pva.trend")}
          hint={t("analytics.pva.trendHint")}
          value={signedMoney(data.trend, currency, locale)}
          valueTone={signedTone(data.trend)}
          badge={<DeltaBadge amount={data.trend} />}
        />
        {hasDistance && (
          <>
            <KpiCard
              label={t("analytics.pva.plannedDistance")}
              hint={t("analytics.pva.plannedDistanceHint")}
              value={metric(
                data.planned_distance_km,
                t("analytics.pva.kmShort"),
                locale,
              )}
            />
            <KpiCard
              label={t("analytics.pva.actualDistance")}
              hint={t("analytics.pva.actualDistanceHint")}
              value={metric(
                data.actual_distance_km,
                t("analytics.pva.kmShort"),
                locale,
              )}
            />
          </>
        )}
        {hasDuration && (
          <>
            <KpiCard
              label={t("analytics.pva.plannedTime")}
              hint={t("analytics.pva.plannedTimeHint")}
              value={metric(plannedHours, t("analytics.pva.hShort"), locale)}
            />
            <KpiCard
              label={t("analytics.pva.actualTime")}
              hint={t("analytics.pva.actualTimeHint")}
              value={metric(actualHours, t("analytics.pva.hShort"), locale)}
            />
          </>
        )}
      </div>

      {/* Plan-vs-fact bar charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PlanVsActualBarChart
          title={t("analytics.pva.distanceKm")}
          planned={data.planned_distance_km}
          actual={data.actual_distance_km}
          unit={t("analytics.pva.kmShort")}
        />
        <PlanVsActualBarChart
          title={t("analytics.pva.timeH")}
          planned={plannedHours}
          actual={actualHours}
          unit={t("analytics.pva.hShort")}
        />
      </div>
    </div>
  );
}
